// Iter 304 — monthly "new deals" digest to Partner Success managers:
// settings (schedule, CC), a preview of who would get what, and Send now.
import { useEffect, useState } from "react";
import { Loader2, Mail, Send, UserX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money, apiError } from "./engineShared";

const CFG_URL = "/commissions/new-deal-digest/config";

export function NewDealDigestDialog({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, day_of_month: 3, hour: 8, cc: "" });
  const [period, setPeriod] = useState("");
  const [preview, setPreview] = useState(null);
  const [unticked, setUnticked] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(CFG_URL).then(({ data }) => {
      setCfg(data);
      setForm({ enabled: !!data.enabled, day_of_month: data.day_of_month, hour: data.hour, cc: (data.cc || []).join(", ") });
      setPeriod(data.default_period || "");
    }).catch((e) => apiError(e, "Could not load digest settings"));
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(CFG_URL, {
        enabled: form.enabled, day_of_month: Number(form.day_of_month), hour: Number(form.hour),
        cc: form.cc.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setCfg(data); toast.success("Digest settings saved");
    } catch (e) { apiError(e, "Could not save"); } finally { setBusy(""); }
  };
  const run = async (dryRun) => {
    if (!dryRun && !window.confirm(`Send the ${preview?.period_name || period} digest to ${eligible.length} manager(s) now?`)) return;
    setBusy(dryRun ? "preview" : "send");
    try {
      const { data } = await api.post(`/commissions/new-deal-digest/send-now?period_label=${encodeURIComponent(period)}&dry_run=${dryRun}`,
        dryRun ? {} : { manager_ids: eligible.map((m) => m.user_id) });
      setPreview(data);
      if (!dryRun) {
        toast.success(data.reason === "sandbox" ? `Sandbox — ${data.sent.length} digest(s) logged, nothing sent`
          : data.reason === "email_disabled" ? "Outbound email is disabled — nothing sent"
          : `Sent to ${data.sent.length} manager(s)${data.failed.length ? `, ${data.failed.length} failed` : ""}`);
      }
    } catch (e) { apiError(e, dryRun ? "Preview failed" : "Send failed"); } finally { setBusy(""); }
  };
  const eligible = (preview?.managers || []).filter((m) => m.email && !unticked.includes(m.user_id));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col" data-testid="digest-dialog">
        <DialogHeader>
          <DialogTitle>New deals digest — partner managers</DialogTitle>
          <DialogDescription>
            A few days into each month, every Partner Success manager gets one email listing the customers that appeared
            for the first time on their partners&apos; statements for the month just closed. Partners without a manager are never emailed.
          </DialogDescription>
        </DialogHeader>
        {!cfg ? <div className="py-8 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
          <div className="-mx-6 flex-1 space-y-4 overflow-y-auto px-6">
            {cfg.sandbox && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="digest-sandbox-note">
                Commissions <b>sandbox</b> is on (Admin → Payment reminders) — a live send only writes to the sandbox log.
              </div>
            )}
            <label className="flex items-start gap-2 rounded border border-slate-200 p-3" data-testid="digest-enabled-row">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="digest-enabled" />
              <span className="text-sm">
                <b>Automatic monthly send</b>
                <span className="block text-xs text-slate-500">Runs on day {form.day_of_month} at {String(form.hour).padStart(2, "0")}:00 Edmonton, covering the previous month.</span>
              </span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Day of month</Label>
                <Input type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} className="h-9" data-testid="digest-day" /></div>
              <div><Label className="text-xs">Hour (Edmonton)</Label>
                <Input type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} className="h-9" data-testid="digest-hour" /></div>
              <div><Label className="text-xs">CC (comma-separated)</Label>
                <Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} placeholder="ops@example.com" className="h-9" data-testid="digest-cc" /></div>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{cfg.last_run_at ? `Last run ${cfg.last_run_at.slice(0, 16).replace("T", " ")} · ${cfg.last_run_summary?.sent?.length ?? 0} sent` : "Never run"}</span>
              <Button size="sm" variant="outline" onClick={save} disabled={busy !== ""} data-testid="digest-save">{busy === "save" && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save settings</Button>
            </div>

            <div className="rounded border border-slate-200 p-3">
              <div className="flex flex-wrap items-end gap-2">
                <div><Label className="text-xs">Statement month</Label>
                  <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 w-40" data-testid="digest-period" /></div>
                <Button size="sm" variant="outline" onClick={() => run(true)} disabled={!period || busy !== ""} data-testid="digest-preview">
                  {busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Preview recipients
                </Button>
              </div>
              {preview && <DigestPreview preview={preview} unticked={unticked} setUnticked={setUnticked} />}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="digest-close">Close</Button>
          <Button onClick={() => run(false)} disabled={!preview || !eligible.length || busy !== ""} data-testid="digest-send">
            {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now to {eligible.length} manager{eligible.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DigestPreview({ preview, unticked, setUnticked }) {
  const toggle = (id) => setUnticked((u) => (u.includes(id) ? u.filter((x) => x !== id) : [...u, id]));
  return (
    <div className="mt-3 space-y-3 text-xs" data-testid="digest-preview-summary">
      <div className="text-slate-600">
        <b>{preview.new_deals}</b> new customer{preview.new_deals === 1 ? "" : "s"} in {preview.period_name} ·{" "}
        <b>{preview.managers.length}</b> manager{preview.managers.length === 1 ? "" : "s"} ·{" "}
        {preview.unassigned.length} partner{preview.unassigned.length === 1 ? "" : "s"} without a manager
        {preview.sent?.length > 0 && <> · <span className="text-emerald-700">sent: {preview.sent.join(", ")}</span></>}
        {preview.failed?.length > 0 && <> · <span className="text-rose-700">failed: {preview.failed.map((f) => f.manager).join(", ")}</span></>}
      </div>
      {preview.managers.length === 0 && <div className="text-slate-400" data-testid="digest-no-managers">No manager has a partner with a new customer this month.</div>}
      {preview.managers.map((m) => (
        <label key={m.user_id} className="flex items-start gap-2 rounded border border-slate-200 bg-white p-2" data-testid={`digest-manager-${m.user_id}`}>
          <input type="checkbox" checked={!!m.email && !unticked.includes(m.user_id)} disabled={!m.email} onChange={() => toggle(m.user_id)} className="mt-0.5" />
          <span className="flex-1">
            <b>{m.name}</b> <span className="text-slate-500">{m.email || "— no email"}</span> · {m.deals_count} new customer{m.deals_count === 1 ? "" : "s"} across {m.partners_count} partner{m.partners_count === 1 ? "" : "s"}
            <ul className="mt-1 space-y-0.5 text-slate-600">
              {m.partners.map((p) => (
                <li key={p.agent}>{p.agent}: {p.domains.join(", ")} <span className="text-slate-400">· {money(p.total)} revenue · {money(p.commission)} commission</span></li>
              ))}
            </ul>
          </span>
        </label>
      ))}
      {preview.unassigned.length > 0 && (
        <div className="rounded border border-dashed border-slate-300 p-2 text-slate-600" data-testid="digest-unassigned">
          <div className="mb-1 flex items-center gap-1 font-medium text-slate-700"><UserX className="h-3.5 w-3.5" /> Left out — no manager</div>
          <ul className="space-y-0.5">
            {preview.unassigned.map((p) => <li key={p.agent}>{p.agent}: {p.domains.join(", ")} <span className="text-slate-400">· {p.reason}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
